import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { CrudService } from '../crud/crud.service';
import { ApiError } from '../error-handling/error.models';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private crudService: CrudService<any>,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getAll(
    apiName: string,
    page: number = 1,
    itemsPerPage: number = 10,
    filters: any = {},
    relations: string[] = [],
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<any | ApiError> {
    return this.crudService
      .getAll(
        apiName,
        page,
        itemsPerPage,
        filters,
        relations,
        optionalQueryParams,
        context
      )
      .pipe(catchError(this.handleError));
  }

  getAllPages(
    apiName: string,
    filters: any = {},
    relations: string[] = [],
    itemsPerPage: number = 100,
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<any | ApiError> {
    return this.getAllPagesFromPage(
      apiName,
      1,
      itemsPerPage,
      filters,
      relations,
      optionalQueryParams,
      context
    );
  }

  private getAllPagesFromPage(
    apiName: string,
    page: number,
    itemsPerPage: number,
    filters: any,
    relations: string[],
    optionalQueryParams: string | null,
    context?: HttpContext
  ): Observable<any | ApiError> {
    return this.getAll(
      apiName,
      page,
      itemsPerPage,
      filters,
      relations,
      optionalQueryParams,
      context
    ).pipe(
      switchMap((response: any) => {
        const members = this.collectionMembers(response);
        const total = response?.totalItems ?? response?.['hydra:totalItems'];
        const hasNextPage = total
          ? page * itemsPerPage < total
          : members.length === itemsPerPage;

        if (!hasNextPage) {
          return of(response);
        }

        return this.getAllPagesFromPage(
          apiName,
          page + 1,
          itemsPerPage,
          filters,
          relations,
          optionalQueryParams,
          context
        ).pipe(map((nextResponse: any) => this.mergeCollectionResponses(response, nextResponse)));
      })
    );
  }

  private collectionKey(response: any): 'member' | 'hydra:member' | null {
    if (Array.isArray(response?.member)) return 'member';
    if (Array.isArray(response?.['hydra:member'])) return 'hydra:member';
    return null;
  }

  private collectionMembers(response: any): any[] {
    const key = this.collectionKey(response);
    return key ? response[key] : [];
  }

  private mergeCollectionResponses(first: any, next: any): any {
    const key = this.collectionKey(first) ?? this.collectionKey(next);
    if (!key) return first;

    return {
      ...first,
      [key]: [...this.collectionMembers(first), ...this.collectionMembers(next)],
      totalItems: first?.totalItems ?? next?.totalItems,
      'hydra:totalItems': first?.['hydra:totalItems'] ?? next?.['hydra:totalItems'],
    };
  }

  getById(
    apiName: string,
    id: string,
    relations: string[] = []
  ): Observable<any | ApiError> {
    return this.crudService
      .getById(apiName, id, relations)
      .pipe(catchError(this.handleError));
  }

  create(
    apiName: string,
    data: any,
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<any | ApiError> {
    return this.crudService
      .create(apiName, data, optionalQueryParams, context)
      .pipe(catchError(this.handleError));
  }

  update(
    apiName: string,
    data: any,
    optionalQueryParams: string | null = null,
    context?: HttpContext
  ): Observable<any | ApiError> {
    return this.crudService
      .update(apiName, data, optionalQueryParams, context)
      .pipe(catchError(this.handleError));
  }

  updateWithNoIdCheck(apiName: string, data: any): Observable<any | ApiError> {
    return this.crudService
      .updateWithNoIdCheck(apiName, data)
      .pipe(catchError(this.handleError));
  }

  updateAction(
    apiName: string,
    data: any,
    action: string
  ): Observable<any | ApiError> {
    return this.crudService
      .updateAction(apiName, data, action)
      .pipe(catchError(this.handleError));
  }

  createAction(
    apiName: string,
    data: any,
    action: string
  ): Observable<any | ApiError> {
    return this.crudService
      .createAction(apiName, data, action)
      .pipe(catchError(this.handleError));
  }

  getAction(
    apiName: string,
    data: any,
    action: string
  ): Observable<any | ApiError> {
    return this.crudService
      .getAction(apiName, data, action)
      .pipe(catchError(this.handleError));
  }

  delete(apiName: string, id: string): Observable<any | ApiError> {
    return this.crudService
      .delete(apiName, id)
      .pipe(tap(() => this.clearImpotsGouvCacheAfterCompanyDelete(apiName)))
      .pipe(catchError(this.handleError));
  }

  private clearImpotsGouvCacheAfterCompanyDelete(apiName: string): void {
    if (apiName !== 'companies') {
      return;
    }

    Object.keys(localStorage)
      .filter((key) => key.startsWith('impots-gouv-messaging:'))
      .forEach((key) => localStorage.removeItem(key));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    // Gestion de l'erreur
    const apiError = this.errorHandlingService.handleError(error);
    // Utilise throwError pour signaler une erreur
    // L'erreur ira directement au bloc error si celui-ci est défini dans le subscribe
    return throwError(() => apiError);
  };

  updateBlob(apiName: string, data: any): Observable<Blob | ApiError> {
    return this.crudService.updateBlob(apiName, data).pipe(
      catchError((error: HttpErrorResponse | ApiError) => {
        const apiError =
          error instanceof HttpErrorResponse
            ? this.errorHandlingService.handleError(error)
            : error;
        return throwError(() => apiError);
      })
    );
  }

  getBlob(apiName: string, data: any): Observable<Blob | ApiError> {
    return this.crudService.getBlob(apiName, data).pipe(
      catchError((error: HttpErrorResponse | ApiError) => {
        const apiError =
          error instanceof HttpErrorResponse
            ? this.errorHandlingService.handleError(error)
            : error;
        return throwError(() => apiError);
      })
    );
  }
  
}
